"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { SpacetimeDBProvider, useSpacetimeDB } from "spacetimedb/react";
import { getSpacetimeToken } from "@/app/actions";
import { buildPortalSpacetimeBuilder } from "@/lib/spacetime";
import BootOverlay from "@/components/boot-overlay";
import {
  markConnected,
  markTokenResolved,
  useBootReady,
} from "@/lib/boot-state";

/**
 * Fetches the OpenAuth token (server action), then mounts the SDK provider.
 * On connectionError the ConnectionManager auto-reconnects with backoff using
 * the LAST builder it was handed — so we re-fetch a fresh token and hand it a
 * new builder (retain() swaps the stored builder) to survive token expiry.
 *
 * BootOverlay is rendered as a stable LAST sibling in both branches so React
 * never remounts it across the token → connected transition — the Lottie
 * animation must load exactly once per session (spec §2). We still deliberately
 * do NOT mount ConnectedProvider with an anonymous builder while the token is
 * in flight: the SDK's ConnectionManager hands back a *live* connection on
 * retain() and keeps reusing whatever builder it was last given, so an
 * anonymous socket would be sticky even after the real token resolves.
 */
export function PortalSpacetimeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [token, setToken] = useState<string | null | undefined>(undefined); // undefined = loading
  useEffect(() => {
    getSpacetimeToken().then(setToken, () => setToken(null));
  }, []);
  useEffect(() => {
    if (token !== undefined) markTokenResolved();
  }, [token]);

  return (
    <>
      {token !== undefined && (
        <ConnectedProvider token={token}>{children}</ConnectedProvider>
      )}
      <BootOverlay />
    </>
  );
}

function ConnectedProvider({
  token,
  children,
}: {
  token: string | null;
  children: React.ReactNode;
}) {
  const [currentToken, setCurrentToken] = useState(token);
  const builder = useMemo(
    () => buildPortalSpacetimeBuilder(currentToken),
    [currentToken],
  );
  return (
    <SpacetimeDBProvider connectionBuilder={builder}>
      <TokenRefresher onFreshToken={setCurrentToken} />
      <ConnectionReporter />
      {children}
    </SpacetimeDBProvider>
  );
}

/** Reports the first successful socket connect to the boot store. */
function ConnectionReporter() {
  const { isActive } = useSpacetimeDB();
  useEffect(() => {
    if (isActive) markConnected();
  }, [isActive]);
  return null;
}

function TokenRefresher({
  onFreshToken,
}: {
  onFreshToken: (t: string | null) => void;
}) {
  const { connectionError } = useSpacetimeDB();
  const refreshing = useRef(false);
  useEffect(() => {
    if (!connectionError || refreshing.current) return;
    refreshing.current = true;
    getSpacetimeToken()
      .then((t) => onFreshToken(t))
      .catch((error) => {
        // Keep the stale builder on a failed refresh — do NOT call
        // onFreshToken here. The SDK's own reconnect backoff will produce a
        // fresh connectionError (a distinct object) on its next attempt,
        // which re-arms this effect (new dep) so we try again then.
        console.warn(
          "PortalSpacetimeProvider: failed to refresh SpacetimeDB token after a connection error",
          error,
        );
      })
      .finally(() => {
        refreshing.current = false;
      });
  }, [connectionError, onFreshToken]);
  return null;
}

export { useSpacetimeDB as usePortalConnection };

/**
 * Boot-once error gate. Before first ready, a connectionError blocks the
 * portal with the full-screen error fallback (retry = reload). AFTER first
 * ready the gate is inert: transient errors heal silently via the SDK's
 * auto-reconnect + TokenRefresher, and content stays mounted (spec §4).
 */
export function SpacetimeGate({
  children,
  errorFallback,
}: {
  children: React.ReactNode;
  errorFallback: (error: Error, retry: () => void) => React.ReactNode;
}) {
  const { connectionError } = useSpacetimeDB();
  const ready = useBootReady();
  if (!ready && connectionError) {
    return (
      <>{errorFallback(connectionError, () => window.location.reload())}</>
    );
  }
  return <>{children}</>;
}
